const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const sectionPlans = [
  {
    section: '.shopify-section--homepage-benefits',
    groups: [
      ['.homepage-benefits__heading, .homepage-benefits__body', 'up'],
      ['.homepage-benefits__item', 'scale'],
      ['.homepage-benefits__shipping-item', 'up'],
    ],
  },
  {
    section: '.shopify-section--homepage-science',
    groups: [
      ['.homepage-science__eyebrow, .homepage-science__heading, .homepage-science__body', 'up'],
      ['.homepage-science__media', 'left'],
      ['.homepage-science__research, .homepage-science__spotlight', 'right'],
      ['.homepage-science__stat-card, .homepage-science__comparison, .homepage-science__logo-tile', 'scale'],
    ],
  },
  {
    section: '.shopify-section--homepage-workday-proof',
    groups: [
      ['.homepage-workday-proof__visual', 'left'],
      ['.homepage-workday-proof__content', 'right'],
      ['.homepage-workday-proof__logo-tile', 'scale'],
    ],
  },
  {
    section: '.shopify-section--active-sitting-experience',
    groups: [
      ['.homepage-active-experience__eyebrow, .homepage-active-experience__heading, .homepage-active-experience__supporting', 'up'],
      ['.homepage-active-experience__media', 'left'],
      ['.homepage-active-experience__benefit', 'right'],
    ],
  },
  {
    section: '.shopify-section--filtered-reviews',
    groups: [
      ['.homepage-filtered-reviews__eyebrow, .homepage-filtered-reviews__heading, .homepage-filtered-reviews__body', 'up'],
      ['.homepage-filtered-reviews__filters', 'up'],
      ['.homepage-filtered-reviews__card', 'scale'],
    ],
  },
  {
    section: '.shopify-section--homepage-chair-comparison',
    groups: [
      ['.homepage-chair-comparison__intro', 'up'],
      ['.homepage-chair-comparison__item', 'scale'],
      ['.homepage-chair-comparison__quiz-wrap', 'up'],
    ],
  },
  {
    section: '.shopify-section--consultation-benefits',
    groups: [
      ['.homepage-consultation-benefits__card', 'left'],
      ['.homepage-consultation-benefits__benefit', 'right'],
    ],
  },
  {
    section: '.shopify-section--qor360-story',
    groups: [
      ['.homepage-qor360-story__media', 'left'],
      ['.homepage-qor360-story__content', 'right'],
    ],
  },
  {
    section: '.shopify-section--homepage-common-questions',
    groups: [
      ['.homepage-common-questions__intro', 'up'],
      ['.homepage-common-questions__item', 'up'],
      ['.homepage-common-questions__cta-wrap', 'up'],
    ],
  },
  {
    section: '.shopify-section--homepage-final-cta',
    groups: [
      ['.homepage-final-cta__eyebrow, .homepage-final-cta__heading, .homepage-final-cta__supporting-copy, .homepage-final-cta__button-wrap', 'up'],
      ['.homepage-final-cta__reassurance-item', 'up'],
    ],
  },
];

const driftSelector = [
  '.homepage-workday-proof__image',
  '.homepage-active-experience__fallback-image',
  '.homepage-science__active-video',
  '.homepage-qor360-story__image',
].join(',');

let revealObserver;
let driftItems = [];
let driftFrame = 0;

function matchesWithin(root, selector) {
  const matches = [];
  if (root.matches?.(selector)) matches.push(root);
  root.querySelectorAll?.(selector).forEach((element) => matches.push(element));
  return matches;
}

function revealImmediately(element) {
  requestAnimationFrame(() => element.classList.add('is-qor-visible'));
}

function observeReveal(element) {
  if (!revealObserver) {
    revealImmediately(element);
    return;
  }

  const rect = element.getBoundingClientRect();
  if (rect.bottom < 0 || rect.top < window.innerHeight * 0.94) {
    revealImmediately(element);
  } else {
    revealObserver.observe(element);
  }
}

function markGroup(section, selector, motion, delayStart) {
  let delay = delayStart;

  section.querySelectorAll(selector).forEach((element) => {
    if (element.dataset.qorReveal) return;

    element.dataset.qorReveal = motion;
    element.style.setProperty('--qor-reveal-delay', `${Math.min(delay, 360)}ms`);
    observeReveal(element);
    delay += 70;
  });
}

function prepareSection(section, plan) {
  section.dataset.qorMotionPrepared = 'true';

  let delay = 0;
  plan.groups.forEach(([selector, motion]) => {
    markGroup(section, selector, motion, delay);
    delay = Math.min(delay + 70, 210);
  });
}

function prepareHero(root = document) {
  const hero = matchesWithin(root, '.shopify-section--homepage-hero')[0];
  if (!hero || hero.dataset.qorMotionPrepared) return;

  hero.dataset.qorMotionPrepared = 'true';
  hero.querySelectorAll('.homepage-hero__content > *').forEach((element, index) => {
    element.dataset.qorReveal = index === 0 ? 'left' : 'up';
    element.style.setProperty('--qor-reveal-delay', `${100 + index * 85}ms`);
    revealImmediately(element);
  });
}

function prepareDrift(root = document) {
  matchesWithin(root, driftSelector).forEach((element) => {
    if (element.dataset.qorDrift) return;
    element.dataset.qorDrift = 'true';
    driftItems.push(element);
  });
}

function updateDrift() {
  driftFrame = 0;
  if (reduceMotion.matches) return;

  const viewportCenter = window.innerHeight / 2;
  driftItems = driftItems.filter((element) => element.isConnected);

  driftItems.forEach((element) => {
    const rect = element.getBoundingClientRect();
    if (rect.bottom < -100 || rect.top > window.innerHeight + 100) return;

    const elementCenter = rect.top + rect.height / 2;
    const progress = Math.max(-1, Math.min(1, (elementCenter - viewportCenter) / window.innerHeight));
    element.style.setProperty('--qor-drift-y', `${progress * -12}px`);
  });
}

function requestDriftUpdate() {
  if (driftFrame) return;
  driftFrame = requestAnimationFrame(updateDrift);
}

function prepareHomepage(root = document) {
  prepareHero(root);

  sectionPlans.forEach((plan) => {
    matchesWithin(root, plan.section).forEach((section) => prepareSection(section, plan));
  });

  prepareDrift(root);
  document.documentElement.classList.add('qor-motion-ready');
  requestDriftUpdate();
}

if (!reduceMotion.matches && 'IntersectionObserver' in window) {
  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-qor-visible');
        revealObserver.unobserve(entry.target);
      });
    },
    {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px',
    },
  );
}

prepareHomepage();

window.addEventListener('scroll', requestDriftUpdate, { passive: true });
window.addEventListener('resize', requestDriftUpdate, { passive: true });
document.addEventListener('shopify:section:load', (event) => prepareHomepage(event.target));

new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      prepareHomepage(node);
      sectionPlans.forEach((plan) => {
        const section = node.closest?.(plan.section);
        if (section) prepareSection(section, plan);
      });
    });
  });
}).observe(document.body, { childList: true, subtree: true });
